import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Meses } from '../../models/Formulacion/indicadores-metas/datos';

export enum PerspectiveIdentifier {
  GENDER = 'gender',
  AGE_RANGE = 'age_range',
  GENERATION = 'generation',
  MARITAL_STATUS = 'marital_status',
  PROFESSION = 'profession',
  ECONOMIC_ACTIVITY = 'economic_activity',
  EDUCATION_LEVEL = 'education_level',
  MUNICIPALITY = 'municipality',
  MEMBER_STATUS = 'member_status',
  BRANCH_OFFICE = 'branch_office',
  REGION = 'region',
  ADVISOR = 'advisor',
}

export enum PERIODICITY {
  MONTH ='month',
  BIMONTH = 'bimonth',
  QUARTER = 'quarter',
  SEMESTER = 'semester',
  YEAR = 'year',
};


@Injectable({
  providedIn: 'root'
})
export class ComercialService {

  private readonly api_url = environment.api_general;    
  private headersSinToken = new HttpHeaders().append('Content-Type', 'application/json');
  private headers = new HttpHeaders().append('Content-Type', 'application/json').append('Authorization', 'Bearer ' + localStorage.getItem('tokenComercial'));

  constructor(
    private readonly http: HttpClient,

  ) {}

  /** Ordena años y meses de forma ascendente para no depender del orden en que los manda el backend. */
  ordenarPeriodos(periods: { year: number; months: number[] }[]){
    return [...(periods ?? [])]
      .sort((a, b) => a.year - b.year)
      .map(y => ({ ...y, months: [...y.months].sort((a, b) => a - b) }));
  }

  /**
   * Recorre los meses del más reciente al más antiguo (hasta `maxIntentos`) y regresa el primero
   * donde `tieneDatos` resuelve true. Si ninguno trae datos, regresa el más reciente.
   * `planos` debe venir ordenado de forma ascendente.
   */
  async ultimoPeriodoConDatos<T extends { an: number; mes: string }>(
    planos: T[],
    tieneDatos: (periodo: string) => Promise<boolean>,
    maxIntentos = 12
  ): Promise<T>{
    const limite = Math.max(0, planos.length - maxIntentos);
    for(let i = planos.length - 1; i >= limite; i--){
      const periodo = `${planos[i].an}-${Meses.getMesesNum(planos[i].mes)}`;
      try{
        if(await tieneDatos(periodo)){
          return planos[i];
        }
      }catch{
        // Si falla ese mes en particular, se prueba con el anterior.
      }
    }
    return planos[planos.length - 1];
  }

  loginAleema(){
    const url = `${this.api_url}auth/login`
    const body = {
      email: "odef.viewer@aleema.com",
      password: "TEST_ODEF-1234#"
    }

    return this.http.post(url, body, {headers: this.headersSinToken})
  }
  loginAleemaCrediguate(){
    const url = `${this.api_url}auth/login`
    const body = {
      email: "ecruz.comercial@friedrichlatam.com",
      password: "Temporal2024!"
    }

    return this.http.post(url, body, {headers: this.headersSinToken})
  }
  loginOdefFinanciera(){
    const url = `${this.api_url}auth/login`
    const body = {
      email: "viewer@odef-financiera-s-a.test",
      password: "test-dev-123"
    }

    return this.http.post(url, body, {headers: this.headersSinToken})
  }
  /**
   * Obtiene las entradas de un catálogo (por ejemplo, `branch_office` para sucursales).
   * Si no se especifica `catalogKey`, según el endpoint, trae todas las entradas disponibles.
   */
  getCatalogEntries(catalogKey: string, token?) {
    return this.http.get(`${this.api_url}catalogs/${catalogKey}`, {
      headers: this.headersSinToken.append('Authorization', 'Bearer ' + token)
    });
  }
  /**
   * Optención de periodos
   */
  getPeriodosAnexo1(token?) {
    return this.http.get(`${this.api_url}/commercial/odef/creditos/a1/periods`, {
      headers: this.headersSinToken.append('Authorization', 'Bearer ' + token)
    });
  }
  /** Periodos reales del endpoint de créditos (commercial/credits), usado en perfil. */
  getPeriodosCreditos(token?) {
    return this.http.get(`${this.api_url}commercial/credits/periods`, {
      headers: this.headersSinToken.append('Authorization', 'Bearer ' + token)
    });
  }
  /** Meta vs real de créditos (Saldos, Cuentas de crédito) por perspectiva (branch_office, region, advisor, ...). */
  getGoalsCredits(params: {
    perspective: PerspectiveIdentifier;
    period: string;
    granularity: PERIODICITY;
    filters?: any;
  }, token?){
    let httpParams = new HttpParams()
      .set('perspective', params.perspective)
      .set('period', params.period)
      .set('granularity', params.granularity);

    if (params.filters && Object.keys(params.filters).length > 0) {
      httpParams = httpParams.set('filters', JSON.stringify(params.filters));
    }

    return this.http.get(`${this.api_url}commercial/goals/credits/counts`, {
      params: httpParams, headers: this.headersSinToken.append('Authorization', 'Bearer ' + token)
    });
  }
  /** Meta vs real de socios (Clientes) por perspectiva (branch_office, region, advisor, ...). */
  getGoalsPartners(params: {
    perspective: PerspectiveIdentifier;
    period: string;
    granularity: PERIODICITY;
    filters?: any;
  }, token?){
    let httpParams = new HttpParams()
      .set('perspective', params.perspective)
      .set('period', params.period)
      .set('granularity', params.granularity);

    if (params.filters && Object.keys(params.filters).length > 0) {
      httpParams = httpParams.set('filters', JSON.stringify(params.filters));
    }

    return this.http.get(`${this.api_url}commercial/goals/partners/counts`, {
      params: httpParams, headers: this.headersSinToken.append('Authorization', 'Bearer ' + token)
    });
  }
  /**
   * Créditos (Saldos) por asesor de una agencia, con su meta incluida.
   * A diferencia de getGoalsCredits, `goals/credits/counts` no soporta `filters` ni trae
   * la agencia de cada asesor; aquí se usa el endpoint real (`credits/counts`) con
   * `include=goal`, que sí acepta `filters={"branch_office":[codigo]}` para acotar por agencia.
   */
  getAsesoresCreditos(branchOfficeCode: number, period: string, granularity: PERIODICITY, token?){
    const httpParams = new HttpParams()
      .set('perspective', PerspectiveIdentifier.ADVISOR)
      .set('period', period)
      .set('granularity', granularity)
      .set('filters', JSON.stringify({ branch_office: [branchOfficeCode] }))
      .set('include', 'goal');

    return this.http.get(`${this.api_url}commercial/credits/counts`, {
      params: httpParams, headers: this.headersSinToken.append('Authorization', 'Bearer ' + token)
    });
  }
  /** Socios (Clientes) por asesor de una agencia, con su meta incluida. Ver getAsesoresCreditos. */
  getAsesoresPartners(branchOfficeCode: number, period: string, granularity: PERIODICITY, token?){
    const httpParams = new HttpParams()
      .set('perspective', PerspectiveIdentifier.ADVISOR)
      .set('period', period)
      .set('granularity', granularity)
      .set('filters', JSON.stringify({ branch_office: [branchOfficeCode] }))
      .set('include', 'goal');

    return this.http.get(`${this.api_url}commercial/partners/counts`, {
      params: httpParams, headers: this.headersSinToken.append('Authorization', 'Bearer ' + token)
    });
  }
  getPeriodosAnexo3(token?) {
    return this.http.get(`${this.api_url}/commercial/odef/creditos/a3/periods`, {
      headers: this.headersSinToken.append('Authorization', 'Bearer ' + token)
    });
  }
  /**
   * Obtiene el conteo de cuentas segmentado por una perspectiva específica.
   *
   * Este método construye la consulta para el backend con base en la perspectiva
   * seleccionada, el periodo de consulta, la granularidad temporal y los filtros
   * adicionales aplicados a otras perspectivas.
   *
   * @param params Configuración de la consulta.
   * @param params.perspective Perspectiva por la cual se desea segmentar la información.
   * Ejemplos: `gender`, `age_range`, `branch_office`.
   * @param params.period Periodo de consulta sobre el cual se realizará la segmentación.
   * formato de YYYY-MM para cuando es por mes
   * @param params.granularity Nivel de agrupación temporal de la consulta.
   * Ejemplos: `month`, `bimonth`, `quarter`, `semester`, `year`.
   * @param params.filters Filtros adicionales por perspectiva. Cada propiedad corresponde
   * a un identificador de perspectiva y su valor es un arreglo de IDs numéricos a filtrar.
   * Ejemplo:
   * ```ts
   * {
   *   gender: [1, 2],
   *   branch_office: [4],
   *   member_status: [1]
   * }
   * ```
   *
   * @returns Observable con la información segmentada de cuentas, agrupada según la
   * perspectiva y granularidad indicadas.
   */
  getCuentas(params: {
    perspective: PerspectiveIdentifier;
    period: string;
    granularity: PERIODICITY;
    filters?: any;
  }, token?) {
    let httpParams = new HttpParams()
      .set('perspective', params.perspective)
      .set('period', params.period)
      .set('granularity', params.granularity);

    if (params.filters && Object.keys(params.filters).length > 0) {
      httpParams = httpParams.set('filters', JSON.stringify(params.filters));
    }
    return this.http.get(`${this.api_url}/commercial/partners/counts`, {
      params: httpParams, headers: this.headersSinToken.append('Authorization', 'Bearer ' + token)
    });
  }

  /**
   * Obtiene el conteo de créditos segmentado por una perspectiva específica.
   *
   * Este método construye la consulta para el backend con base en la perspectiva
   * seleccionada, el periodo de consulta, la granularidad temporal y los filtros
   * adicionales aplicados a otras perspectivas.
   *
   * @param params Configuración de la consulta.
   * @param params.perspective Perspectiva por la cual se desea segmentar la información.
   * Ejemplos: `region`, `branch_office`.
   * @param params.period Periodo de consulta sobre el cual se realizará la segmentación.
   * formato de YYYY-MM para cuando es por mes
   * @param params.granularity Nivel de agrupación temporal de la consulta.
   * Ejemplos: `month`, `bimonth`, `quarter`, `semester`, `year`.
   * @param params.filters Filtros adicionales por perspectiva. Cada propiedad corresponde
   * a un identificador de perspectiva y su valor es un arreglo de IDs numéricos a filtrar.
   * Ejemplo:
   * ```ts
   * {
   *   region: [4],
   * }
   * ```
   *
   * @returns Observable con la información segmentada de créditos, agrupada según la
   * perspectiva y granularidad indicadas.
   */
  getCreditosCounts(params: {
    perspective: PerspectiveIdentifier;
    period: string;
    granularity: PERIODICITY;
    filters?: any;
  }, token?){
    let httpParams = new HttpParams()
      .set('perspective', params.perspective)
      .set('period', params.period)
      .set('granularity', params.granularity);

    if (params.filters && Object.keys(params.filters).length > 0) {
      httpParams = httpParams.set('filters', JSON.stringify(params.filters));
    }

    return this.http.get(`${this.api_url}commercial/credits/counts`, {
      params: httpParams, headers: this.headersSinToken.append('Authorization', 'Bearer ' + token)
    });
  }

  /**
   * Obtiene los cálculos del anexo 1.
   *
   * Este método construye la consulta para el backend con base en la perspectiva
   * seleccionada, el periodo de consulta, la granularidad temporal y los filtros
   * adicionales aplicados a otras perspectivas.
   *
   * @param params Configuración de la consulta.
   * @param params.perspective Perspectiva por la cual se desea segmentar la información.
   * Ejemplos: `region`, `branch_office`.
   * @param params.period Periodo de consulta sobre el cual se realizará la segmentación.
   * formato de YYYY-MM para cuando es por mes
   * @param params.granularity Nivel de agrupación temporal de la consulta.
   * Ejemplos: `month`, `bimonth`, `quarter`, `semester`, `year`.
   * @param params.filters Filtros adicionales por perspectiva. Cada propiedad corresponde
   * a un identificador de perspectiva y su valor es un arreglo de IDs numéricos a filtrar.
   * Ejemplo:
   * ```ts
   * {
   *   branch_office: [4],
   * }
   * ```
   *
   * @returns Observable con la información segmentada de cuentas, agrupada según la
   * perspectiva y granularidad indicadas.
   */
  getCreditoA1(params: {
    perspective: PerspectiveIdentifier;
    period: string;
    granularity: PERIODICITY;
    filters?: any;
  }, token?){
    let httpParams = new HttpParams()
      .set('perspective', params.perspective)
      .set('period', params.period)
      .set('granularity', params.granularity);

    if (params.filters && Object.keys(params.filters).length > 0) {
      httpParams = httpParams.set('filters', JSON.stringify(params.filters));
    }

    return this.http.get(`${this.api_url}commercial/odef/creditos/a1`, {
      params: httpParams, headers: this.headersSinToken.append('Authorization', 'Bearer ' + token)
    });
  }
  /**
   * Obtiene los cálculos del anexo 1.
   *
   * Este método construye la consulta para el backend con base en la perspectiva
   * seleccionada, el periodo de consulta, la granularidad temporal y los filtros
   * adicionales aplicados a otras perspectivas.
   *
   * @param params Configuración de la consulta.
   * @param params.perspective Perspectiva por la cual se desea segmentar la información.
   * Ejemplos: `region`, `branch_office`.
   * @param params.period Periodo de consulta sobre el cual se realizará la segmentación.
   * formato de YYYY-MM para cuando es por mes
   * @param params.granularity Nivel de agrupación temporal de la consulta.
   * Ejemplos: `month`, `bimonth`, `quarter`, `semester`, `year`.
   * @param params.filters Filtros adicionales por perspectiva. Cada propiedad corresponde
   * a un identificador de perspectiva y su valor es un arreglo de IDs numéricos a filtrar.
   * Ejemplo:
   * ```ts
   * {
   *   branch_office: [4],
   * }
   * ```
   *
   * @returns Observable con la información segmentada de cuentas, agrupada según la
   * perspectiva y granularidad indicadas.
   */
  getCreditoA3(params: {
    perspective: PerspectiveIdentifier;
    period: string;
    granularity: PERIODICITY;
    filters?: any;
  }, token?){
    let httpParams = new HttpParams()
      .set('perspective', params.perspective)
      .set('period', params.period)
      .set('granularity', params.granularity);

    if (params.filters && Object.keys(params.filters).length > 0) {
      httpParams = httpParams.set('filters', JSON.stringify(params.filters));
    }

    return this.http.get(`${this.api_url}commercial/odef/creditos/a3`, {
      params: httpParams, headers: this.headersSinToken.append('Authorization', 'Bearer ' + token)
    });
  }
  /**
   * Obtiene los cálculos del anexo 1.
   *
   * Este método construye la consulta para el backend con base en la perspectiva
   * seleccionada, el periodo de consulta, la granularidad temporal y los filtros
   * adicionales aplicados a otras perspectivas.
   *
   * @param params Configuración de la consulta.
   * @param params.perspective Perspectiva por la cual se desea segmentar la información.
   * Ejemplos: `region`, `branch_office`.
   * @param params.period Periodo de consulta sobre el cual se realizará la segmentación.
   * formato de YYYY-MM para cuando es por mes
   * @param params.granularity Nivel de agrupación temporal de la consulta.
   * Ejemplos: `month`, `bimonth`, `quarter`, `semester`, `year`.
   * @param params.filters Filtros adicionales por perspectiva. Cada propiedad corresponde
   * a un identificador de perspectiva y su valor es un arreglo de IDs numéricos a filtrar.
   * Ejemplo:
   * ```ts
   * {
   *   branch_office: [4],
   * }
   * ```
   *
   * @returns Observable con la información segmentada de cuentas, agrupada según la
   * perspectiva y granularidad indicadas.
   */
  getAhorrosA3(params: {
    perspective: PerspectiveIdentifier;
    period: string;
    granularity: PERIODICITY;
    filters?: any;
  }, token?){
    let httpParams = new HttpParams()
      .set('perspective', params.perspective)
      .set('period', params.period)
      .set('granularity', params.granularity);

    if (params.filters && Object.keys(params.filters).length > 0) {
      httpParams = httpParams.set('filters', JSON.stringify(params.filters));
    }

    return this.http.get(`${this.api_url}commercial/odef/ahorros/a3`, {
      params: httpParams, headers: this.headersSinToken.append('Authorization', 'Bearer ' + token)
    });
  }
}
